import { useState, useEffect } from 'react';
import { useData } from './useData';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const isMobileDevice = (): boolean => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /android|iphone|ipad|ipod|windows phone/.test(userAgent);
};

const isIOS = (): boolean => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
};

const isInStandaloneMode = (): boolean => {
    return (
        window.matchMedia('(display-mode: standalone)').matches ||
        (navigator as Navigator & { standalone?: boolean }).standalone === true
    );
};

export function useInstallPrompt() {
    const { allSettings } = useData();
    const isPwaModuleActive = allSettings?.data?.module_pwa?.active;

    const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [isInstallable, setIsInstallable] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);
    const [isIOSDevice, setIsIOSDevice] = useState(false);

    useEffect(() => {
        // check if already in standalone mode (installed)
        if (isInStandaloneMode()) {
            setIsInstalled(true);
            return;
        }

        // check if iOS device
        if (isIOS()) {
            setIsIOSDevice(true);
            setIsInstallable(true);
            return;
        }

        // waiting for beforeinstallprompt event

        const handleBeforeInstallPrompt = (e: Event) => {
            // beforeinstallprompt event fired
            e.preventDefault();
            setInstallPrompt(e as BeforeInstallPromptEvent);
            setIsInstallable(true);
        };

        const handleAppInstalled = () => {
            setIsInstalled(true);
            setIsInstallable(false);
            setInstallPrompt(null);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const promptInstall = async (): Promise<boolean> => {
        if (!installPrompt) return false;

        try {
            await installPrompt.prompt();
            const result = await installPrompt.userChoice;

            if (result.outcome === 'accepted') {
                setIsInstalled(true);
                setIsInstallable(false);
                setInstallPrompt(null);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Install prompt error:', error);
            return false;
        }
    };

    const dismissPrompt = () => {
        setIsInstallable(false);
        // Store dismissal in localStorage to not show again for a while
        localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    };

    // Check if user has recently dismissed the prompt
    const isDismissed = (): boolean => {
        const dismissed = localStorage.getItem('pwa-install-dismissed');
        if (!dismissed) return false;

        const dismissedTime = parseInt(dismissed, 10);
        const dayInMs = 24 * 60 * 60 * 1000;
        const dismissalDays = allSettings?.data?.module_pwa?.show_prompt_after_dismissal_in_days ?? 7;
        const dismissalMs = dismissalDays * dayInMs;

        return Date.now() - dismissedTime < dismissalMs;
    };

    return {
        isInstallable: isPwaModuleActive && isMobileDevice() && isInstallable && !isDismissed(),
        isInstalled,
        isIOSDevice,
        promptInstall,
        dismissPrompt,
    };
}
